// home.js
//
// Structured page content for the Home_Page. Editorial copy, imagery
// references, and the page's SEO metadata live here rather than being
// hardcoded inside component templates, so content changes need no redesign
// (Requirements 11.2, 10.1, 10.2).
//
// `HomePage.vue` and its section components import this module. Project content
// is intentionally NOT stored here — featured projects always come from the
// Projects API (Requirements 11.1, 11.3).
//
// Copy and imagery are sourced from the existing marketing components
// (Landing1–4, ContactUs) rather than newly invented.

// The homepage services summary mirrors the capability areas described on the
// Services page, so the names a visitor sees here match the page they land on
// when they follow through. (The narrower `SERVICE_CATEGORIES` enumeration is
// used for tagging and filtering projects, not for this marketing summary.)
import { services as servicesContent } from './services'

// Imagery references. Imported so webpack resolves them to hashed asset URLs;
// each pairs the image with descriptive alt text (Requirement 9.3).
import heroImage from 'assets/img/parallax/aerial2.jpg'
import metroImage from 'assets/img/metro/cbdnorth.jpg'
import drainageImage from 'assets/img/metro/eastern.jpg'

/**
 * The Home_Page content model.
 *
 * @typedef {Object} HomeContent
 * @property {Object} hero          - full-bleed hero (Requirement 4.1)
 * @property {Object} services      - Service_Categories summary (Requirement 4.2)
 * @property {Object} featuredProjects - heading/intro for the API-sourced section (4.3)
 * @property {Object} credibility   - experience/clients/partners (Requirement 4.4)
 * @property {Object} closingCta    - closing CTA to Contact (Requirement 4.5)
 * @property {import('./seo').PageSeo} seo - per-page SEO metadata
 */

export const home = {
  hero: {
    image: {
      src: heroImage,
      alt: 'Aerial survey imagery of a developed landscape captured by ThinkSpatial'
    },
    eyebrow: 'We are spatial information specialists',
    headline: 'Build smarter and faster',
    subhead:
      'ThinkSpatial integrates traditional surveying with advanced data management, real-time monitoring, and web-based collaboration tools. From subsurface utilities to 3D models, we provide the insights that keep projects running smoothly, efficiently, and on budget.',
    primaryCta: { label: 'Start an enquiry', to: '/contact' },
    secondaryCta: { label: 'View our projects', to: '/projects' }
  },

  services: {
    heading: 'What we do',
    intro:
      'From the ground to the air, we deliver spatial services across the full project lifecycle.',
    // Derived from the Services page capability areas so the two stay in step
    // automatically — adding an area there surfaces it here (Requirement 4.2).
    categories: servicesContent.areas.map((area) => ({
      name: area.title,
      description: area.summary
    })),
    // Through-link to the full capability set on the Services page.
    cta: { label: 'Explore all our services', to: '/services' }
  },

  featuredProjects: {
    heading: 'Featured projects',
    intro:
      'A selection of recent work, drawn live from our project records.',
    emptyText: 'Featured projects will appear here soon.'
  },

  credibility: {
    heading: 'Trusted on landmark infrastructure',
    intro:
      'For more than two decades ThinkSpatial has supported major surveying, infrastructure, and asset programs across Australia.',
    // Concrete credentials from the capability statement — the proof points
    // that matter most to infrastructure and construction clients.
    credentials: [
      {
        title: 'AusGeoid09 with Geoscience Australia',
        body:
          'Involved in developing AusGeoid09, alongside geoid modelling projects for large water authorities.'
      },
      {
        title: 'AS5488 compliant utility data',
        body:
          'Underground services captured, QA-checked, and delivered to the AS5488 standard.'
      },
      {
        title: 'Crews that scale to 6–15 surveyors',
        body:
          'Full-time resourcing for large infrastructure projects, including night shift, weekends, and occupations.'
      },
      {
        title: 'High-precision GNSS processing',
        body:
          'Survey control established and monitored using Bernese GNSS Processing Software.'
      }
    ],
    highlights: [
      {
        title: 'Melbourne Metro Rail Authority',
        image: {
          src: metroImage,
          alt: 'Melbourne Metro Rail alignment through the CBD'
        },
        body:
          'John Holland Group was awarded the MMRA Early Works contract, with ThinkSpatial supplying survey and spatial services across the alignment.'
      },
      {
        title: 'Penrith Drainage Survey',
        image: {
          src: drainageImage,
          alt: 'Asset capture survey of an urban drainage network'
        },
        body:
          'Commissioned by Penrith Council, ThinkSpatial completed a comprehensive asset capture survey of the council’s drainage network.'
      }
    ]
  },

  closingCta: {
    heading: 'Have a project in mind?',
    body:
      'Tell us what you are working on and our team will help you scope the right spatial approach.',
    cta: { label: 'Contact us', to: '/contact' }
  },

  /** @type {import('./seo').PageSeo} */
  seo: {
    title: 'ThinkSpatial — Spatial information specialists',
    description:
      'ThinkSpatial delivers surveying, GIS, UAV mapping, and spatial software across Australia, combining engineering expertise with modern spatial technology.',
    og: {
      title: 'ThinkSpatial — Spatial information specialists',
      description:
        'Surveying, asset capture, UAV mapping, automated monitoring, and spatial software from ThinkSpatial.',
      image: heroImage
    }
  }
}

export default home
